import LiveTvIcon from '@mui/icons-material/LiveTv';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { channelLogoUrl } from '../../api/client';
import type { LiveTvChannel, LiveTvProgram, LiveTvSearchResult } from '../../api/types';
import PlayerOverlay from '../player/PlayerOverlay';
import ProgrammeDialog from './ProgrammeDialog';
import { programmeTimes } from './programmeTimes';

/// What the search found on live television, from every tuner plugin: channels to switch on
/// straight away, and programmes - on now or later - that open the way the guide opens them.
export default function LiveTvSearchResults({ result }: { result: LiveTvSearchResult }) {
  const { t, i18n } = useTranslation();
  const [opened, setOpened] = useState<{ program: LiveTvProgram; channel: LiveTvChannel }>();
  const [watching, setWatching] = useState<LiveTvChannel>();
  // Taken once: the list is a snapshot of the search, not a clock.
  const [now] = useState(Date.now);

  return (
    <Stack spacing={3}>
      {result.channels.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="h3">{t('search.channels')}</Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            }}
          >
            {result.channels.map((channel) => (
              <ButtonBase
                key={`${channel.providerId}:${channel.id}`}
                onClick={() => setWatching(channel)}
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  minHeight: 64,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  textAlign: 'start',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ChannelLogo channel={channel} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {channel.name}
                  </Typography>
                  {channel.number && (
                    <Typography variant="caption" color="text.secondary">
                      {channel.number}
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Stack>
      )}

      {result.programmes.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="h3">{t('search.programmes')}</Typography>

          <Stack spacing={1}>
            {result.programmes.map(({ program, channel }) => {
              const onNow = new Date(program.startsAt).getTime() <= now;

              return (
                <ButtonBase
                  key={`${channel.providerId}:${program.id}`}
                  onClick={() => setOpened({ program, channel })}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    p: 1.5,
                    minHeight: 64,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textAlign: 'start',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ChannelLogo channel={channel} />

                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {program.title}
                      {program.episodeTitle && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {` · ${program.episodeTitle}`}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`${channel.name} · ${programmeTimes(program, i18n.language)}`}
                    </Typography>
                  </Box>

                  {onNow && <Chip size="small" color="error" label={t('search.onNow')} />}
                </ButtonBase>
              );
            })}
          </Stack>
        </Stack>
      )}

      <ProgrammeDialog
        opened={opened}
        onClose={() => setOpened(undefined)}
        onWatch={(channel) => {
          setOpened(undefined);
          setWatching(channel);
        }}
      />

      {watching && (
        <PlayerOverlay target={{ kind: 'live', channel: watching }} onClose={() => setWatching(undefined)} />
      )}
    </Stack>
  );
}

function ChannelLogo({ channel }: { channel: LiveTvChannel }) {
  const theme = useTheme();
  const [failed, setFailed] = useState(false);

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
      }}
    >
      {channel.logo && !failed ? (
        <Box
          component="img"
          src={channelLogoUrl(channel.logo, theme.palette.mode)}
          alt=""
          onError={() => setFailed(true)}
          sx={{ width: 40, height: 40, objectFit: 'contain' }}
        />
      ) : (
        <LiveTvIcon />
      )}
    </Box>
  );
}
